'use client'

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Flex, Text } from '@chakra-ui/react'
import { ProductCategory } from '@leid/api-products'
import { useRouter } from 'next/navigation'

import { BrowsePageSearchParams } from 'app/(pages)/browse/models'
import Container from 'app/components/shared/Container'
import { convertSerachParamsToURLSearchParams } from 'app/utils/urls'
import { getCategoryURLParts } from 'components/nav/CategoriesMenu/utils/categories'
import { ButtonText } from 'components/ui/Buttons/Button'

import CategoriesModal from '../CategoriesModal'
import CategoriesSlider from '../CategoriesSlider'

type Props = {
  location: number | undefined

  categories: ProductCategory[]
  parentCategories?: ProductCategory[]
  startTransition: (cb: () => void) => void
}

const CategoriesCards = ({ categories, parentCategories, location, startTransition }: Props): JSX.Element => {
  const { t } = useTranslation('common')

  const getSelectedCategoryId = (): number | undefined => {
    if (parentCategories) {
      const selectedCategory = categories.find((s) => parentCategories.some((x) => x.id === s.id))
      return selectedCategory?.id
    }

    return undefined
  }

  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>(getSelectedCategoryId())

  const router = useRouter()

  const handleSelectCategory = (newCategory: ProductCategory) => {
    setSelectedCategory(newCategory.id)

    // We want to clear search term on category change
    const searchParams: BrowsePageSearchParams = {
      location: location,
      category: undefined,
      searchTerm: undefined,
      manager: undefined,
      page: undefined,
    }

    const url = newCategory.id !== selectedCategory ? `/${getCategoryURLParts(newCategory, categories)}` : ''
    const params = convertSerachParamsToURLSearchParams(searchParams)
    startTransition(() => {
      router.push(`/browse${url}?${params.toString()}`)
    })
  }

  const toggleMobileMenu = () => {
    setIsCategoriesModalOpen((prevState) => !prevState)
    !isCategoriesModalOpen ? (document.body.style.overflow = 'hidden') : (document.body.style.overflow = 'auto')
  }

  return (
    <Box marginTop="20px" marginBottom="60px" display={{ base: 'block', md: 'none' }}>
      <Container>
        <Flex marginTop="36px" justifyContent="space-between" alignItems="center">
          <Text fontSize="sm">{t('categories')}</Text>
          <ButtonText onClick={toggleMobileMenu}>{t('showAll')}</ButtonText>
        </Flex>
        <CategoriesModal
          isOpen={isCategoriesModalOpen}
          handleSelectCategory={handleSelectCategory}
          onClose={toggleMobileMenu}
          selectedCategory={selectedCategory}
          categories={categories}
        />
      </Container>
      <CategoriesSlider
        handleSelectCategory={handleSelectCategory}
        selectedCategory={selectedCategory}
        categories={categories}
      />
    </Box>
  )
}

export default CategoriesCards
